/**
 * Where an invitation link lands.
 *
 * This is the *only* screen in the app a person reaches before they belong
 * anywhere, and usually before they have signed in at all — it is the address in
 * an email sent to somebody who has never heard of this app. Everything about it
 * follows from that.
 *
 * **It is outside the app shell.** `__root.tsx` gates every other route on there
 * being a session and a tenant; this one cannot be, because the whole point is to
 * be reachable without either. It draws its own page, like `SignIn` and
 * `Onboarding` do.
 *
 * **A signed-out visitor is shown a sign-in button, not redirected to one.** A
 * redirect loses the invitation id, which is the only thing the link carried, and
 * a person bounced to a bare sign-in screen has no idea why they are being asked.
 * Signing in from here comes back to here, because the URL never changed.
 *
 * **The invitation cannot be read until they are signed in as the right person.**
 * The rules allow a read only to the address the invitation names or to a member
 * of the tenant (see firestore.rules) — deliberately, because an invitation id in
 * an email would otherwise be a way to enumerate kennel names. So the three
 * states this screen has to draw are: no session, a session belonging to somebody
 * else, and a readable invitation. Each says something different and useful; a
 * single "something went wrong" for all three is what makes this kind of link
 * infuriating.
 *
 * The accept itself is `acceptInvite`, the same callable the onboarding screen
 * uses — see api/invites.ts. Success needs no navigation: the membership arrives
 * through `TenantProvider`'s listener on `users/{uid}`, at which point the root
 * route has a tenant and renders the app.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { acceptInvite, useInvitesForEmail } from '@/api/invites.ts';
import { useAuth } from '@/auth/AuthProvider.tsx';
import { CollarStrip, Wordmark } from '@/components/Brand.tsx';
import { LanguagePicker } from '@/components/LanguagePicker.tsx';
import { Button, buttonClasses, Panel } from '@/components/ui.tsx';
import { asDate } from '@/lib/dates.ts';
import { expiryOf, useNow } from '@/lib/expiry.ts';

export const Route = createFileRoute('/join/$inviteId')({
  component: JoinPage,
});

function JoinPage() {
  const { t } = useTranslation();
  const { inviteId } = Route.useParams();
  const { status, user, signingIn, signInWithGoogle, signOut } = useAuth();

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-8 px-6 py-10">
      <header className="flex items-start justify-between gap-4">
        <div>
          <CollarStrip className="mb-6" />
          <h1 className="text-2xl leading-none">
            <Wordmark />
          </h1>
        </div>
        {/* A person arriving from an email may not be reading the language the
            invitation was written in. This is the one control that has to be on
            this screen whatever else is. */}
        <LanguagePicker />
      </header>

      {status === 'loading' ? (
        // The third auth state — not yet known. Never a sign-in prompt for that
        // window: an invited person shown "sign in" for a frame and then shown
        // their invitation reads as an app that changed its mind. See app.md.
        <p className="text-ink-faint">{t('common.loading')}</p>
      ) : user === null ? (
        <SignInFirst signingIn={signingIn} onSignIn={() => void signInWithGoogle()} />
      ) : (
        <Waiting inviteId={inviteId} email={user.email ?? ''} onSignOut={() => void signOut()} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * No session. The invitation is not readable yet and nothing about it can be
 * shown — not even which kennel it is for, which is a limit of the rules and not
 * an oversight: the id in this URL must not be a lookup key for a stranger.
 */
const SignInFirst = ({
  signingIn,
  onSignIn,
}: {
  signingIn: boolean;
  onSignIn: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-bold tracking-[-0.01em]">{t('join.title')}</h2>
        <p className="mt-1.5 text-sm text-ink-soft">{t('join.signInLead')}</p>
      </div>
      <Button full disabled={signingIn} onClick={onSignIn}>
        {signingIn ? t('auth.signingIn') : t('auth.signIn')}
      </Button>
      <p className="text-sm text-ink-faint">{t('join.sameAddress')}</p>
    </section>
  );
};

/**
 * Signed in — so the invitation either resolves or it does not.
 *
 * Resolved through the by-email query rather than a document read of the id in
 * the URL, and that is deliberate: it is the query the onboarding screen already
 * uses, it is the one the rules are shaped for, and its miss case is exactly the
 * answer this screen needs to give — "there is no invitation here for *you*".
 * A document read would have to distinguish a permission error from a missing
 * document to say the same thing, and permission errors are not reliable enough
 * to build copy on.
 */
const Waiting = ({
  inviteId,
  email,
  onSignOut,
}: {
  inviteId: string;
  email: string;
  onSignOut: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending } = useInvitesForEmail(email);
  const [joining, setJoining] = useState(false);
  const [failed, setFailed] = useState(false);
  const now = useNow();

  const invite = (data ?? []).find((one) => one.id === inviteId) ?? null;
  const { expired, daysLeft } = expiryOf(invite?.expiresAt ?? now, now);

  if (isPending) return <p className="text-ink-faint">{t('common.loading')}</p>;

  // Not for this person, or gone. One message for both, for the reason
  // `acceptInvite` gives: distinguishing them would confirm to a stranger that an
  // invitation exists, and with it that an address is a member of a kennel.
  if (invite === null) {
    return (
      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-[-0.01em]">{t('join.missingTitle')}</h2>
          <p className="mt-1.5 text-sm text-ink-soft">{t('join.missingLead', { email })}</p>
        </div>
        <Button variant="secondary" full onClick={onSignOut}>
          {t('join.useAnotherAccount')}
        </Button>
        {/* A person who is already in a kennel and followed a stale link should
            not be stranded on this screen. */}
        <Link to="/" className={buttonClasses({ variant: 'quiet', full: true })}>
          {t('join.toApp')}
        </Link>
      </section>
    );
  }

  const join = () => {
    setFailed(false);
    setJoining(true);
    acceptInvite(invite.id)
      .then(() =>
        // Unlike the onboarding screen — which is unmounted by the membership
        // arriving — this route stays matched, and a person who has just joined
        // should be looking at the kennel rather than at the invitation they
        // accepted. `replace`, so Back does not return to a spent link.
        navigate({ to: '/', replace: true }),
      )
      .catch(() => {
        setJoining(false);
        setFailed(true);
      });
  };

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-bold tracking-[-0.01em]">{t('join.title')}</h2>

      <Panel className="flex flex-col gap-3">
        <div>
          <p className="text-lg font-semibold">{invite.tenantName}</p>
          <p className="text-sm text-ink-soft">
            {t('invite.asRole', { role: t(`role.${invite.role}`).toLocaleLowerCase() })}
          </p>
        </div>

        <p className="text-sm text-ink-faint">
          {expired ? t('invite.expired') : t('invite.expiresIn', { count: daysLeft })}
          <span aria-hidden> · </span>
          {asDate(invite.expiresAt)}
        </p>

        {expired ? (
          <p className="text-sm text-alert">{t('join.expiredLead')}</p>
        ) : (
          <Button full disabled={joining} onClick={join}>
            {joining ? t('invite.accepting') : t('invite.accept')}
          </Button>
        )}

        {failed && (
          <p role="alert" className="text-sm text-alert">
            {t('invite.acceptFailed')}
          </p>
        )}
      </Panel>

      <p className="text-center text-sm text-ink-faint">{t('auth.signedInAs', { email })}</p>
      <Button variant="quiet" className="text-sm" onClick={onSignOut}>
        {t('join.useAnotherAccount')}
      </Button>
    </section>
  );
};
